"use client";

import { useState } from "react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Session } from "next-auth";
import { UserProfile } from "@/utils/types";
import { verifyEmailService } from "@/services/verification.service.ts";

interface EmailVerificationSectionProps {
  profileData: UserProfile | null;
  session: Session | null;
  onProfileUpdated: () => void;
}

export function EmailVerificationSection({
  profileData,
  session,
  onProfileUpdated,
}: EmailVerificationSectionProps) {
  const [verificationCode, setVerificationCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [verificationError, setVerificationError] = useState("");

  const isVerified = profileData?.emailVerified;

  async function handleVerify() {
    if (!verificationCode.trim()) {
      setVerificationError("Please enter the verification code");
      return;
    }

    setVerifying(true);
    setVerificationError("");
    setVerificationSuccess(false);

    try {
      const email = profileData?.email || session?.user.email;
      if (!email) {
        setVerificationError("Email address not found");
        return;
      }

      const result = await verifyEmailService({
        email,
        code: verificationCode.trim(),
      });

      if (result.success) {
        setVerificationSuccess(true);
        onProfileUpdated();
      } else {
        setVerificationError(result.message || result.error || "Verification failed");
      }
    } catch (err) {
      console.error("[EmailVerificationSection] Error verifying email:", err);
      setVerificationError("An error occurred during verification");
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div
      style={{
        background: "#0e0e0e",
        border: "1px solid #1c1c1c",
        borderRadius: "14px",
        overflow: "hidden",
        marginBottom: "10px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "18px 20px",
          borderBottom: "1px solid #1c1c1c",
        }}
      >
        <div>
          <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em", color: "#c8c3ba", margin: 0 }}>
            EMAIL VERIFICATION
          </p>
          <p style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.02em", marginTop: "2px" }}>
            Verify your email to access all features
          </p>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "18px 20px" }}>
        {/* Status row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 14px",
            borderRadius: "8px",
            marginBottom: isVerified ? "0" : "16px",
            ...(isVerified
              ? {
                  background: "#0d1a12",
                  border: "1px solid #1a3a26",
                }
              : {
                  background: "#1a0e0e",
                  border: "1px solid #3a1818",
                }),
          }}
        >
          {isVerified ? (
            <>
              <CheckCircleIcon style={{ width: "14px", height: "14px", color: "#5aaa7a", flexShrink: 0 }} />
              <span style={{ fontSize: "11px", fontWeight: 500, color: "#5aaa7a" }}>
                Your email has been verified
              </span>
            </>
          ) : (
            <>
              <XCircleIcon style={{ width: "14px", height: "14px", color: "#a85555", flexShrink: 0 }} />
              <span style={{ fontSize: "11px", fontWeight: 500, color: "#a85555" }}>
                Your email is not verified
              </span>
            </>
          )}
        </div>

        {!isVerified && (
          <div>
            <p style={{ fontSize: "11px", color: "#4a4a4a", marginBottom: "12px", lineHeight: "1.5" }}>
              A verification code has been sent to your email. Enter it below to verify your account.
            </p>

            <div style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
              <input
                placeholder="Enter verification code"
                value={verificationCode}
                onChange={(e) => { setVerificationCode(e.target.value); }}
                style={{
                  fontFamily: "monospace",
                  fontSize: "12px",
                  color: "#e8e3da",
                  background: "#0a0a0a",
                  border: "1px solid #1c1c1c",
                  borderRadius: "8px",
                  padding: "9px 14px",
                  outline: "none",
                  flex: 1,
                  minWidth: 0,
                }}
                aria-label="Verification code"
              />
              <button
                onClick={() => { void handleVerify(); }}
                disabled={verifying}
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#051a0e",
                  background: verifying ? "#2a9a5a" : "#3ecf7e",
                  border: "none",
                  borderRadius: "8px",
                  padding: "9px 18px",
                  cursor: verifying ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  opacity: verifying ? 0.7 : 1,
                  transition: "opacity 0.15s",
                }}
              >
                {verifying ? "Verifying..." : "Verify"}
              </button>
            </div>

            {verificationError && (
              <p style={{ fontSize: "11px", color: "#a85555", marginTop: "8px" }}>
                {verificationError}
              </p>
            )}
            {verificationSuccess && (
              <p style={{ fontSize: "11px", color: "#5aaa7a", marginTop: "8px" }}>
                Email verified successfully!
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
